// src/modules/Investors/pages/InvestorLogin.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../config/supabase';
import { FaGoogle, FaSpinner } from 'react-icons/fa';
import logoVega from '../../../assets/logovega.svg';

const Container = styled.div`
  min-height: 100vh;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  width: 100%;
  max-width: 380px;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
`;

const Logo = styled.div`
  margin-bottom: 40px;

  img {
    height: 32px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 32px;
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px 24px;
  background: #111;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 200ms;

  svg {
    font-size: 16px;
  }

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const BackLink = styled.a`
  display: inline-block;
  margin-top: 24px;
  font-size: 13px;
  color: #999;
  text-decoration: none;
  transition: color 200ms;

  &:hover {
    color: #111;
  }
`;

const InvestorLogin = () => {
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const from = location.state?.from?.pathname || '/investidores';
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Error checking session:', error);
    } finally {
      setCheckingSession(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/investidores`
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <Container>
        <LoginCard>
          <Logo>
            <img src={logoVega} alt="Vega Robotics" />
          </Logo>
          <FaSpinner style={{ fontSize: 20, color: '#999', animation: 'spin 1s linear infinite' }} />
        </LoginCard>
      </Container>
    );
  }

  return (
    <Container>
      <LoginCard>
        <Logo>
          <img src={logoVega} alt="Vega Robotics" />
        </Logo>

        <Title>Portal do Investidor</Title>
        <Subtitle>Acesso exclusivo</Subtitle>

        <GoogleButton onClick={handleGoogleLogin} disabled={loading}>
          {loading ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaGoogle />
          )}
          {loading ? 'Conectando...' : 'Entrar com Google'}
        </GoogleButton>

        <BackLink href="/">&larr; Voltar</BackLink>
      </LoginCard>
    </Container>
  );
};

export default InvestorLogin;
